import { AIRLINE_COLORS, time, dur, money, dateLabel, instantOff } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Utensils, BadgeCheck } from "lucide-react";

const DealPrice = ({ offer, price }) => {
  const { config } = useAuth();
  const n = Math.max(1, Math.round((offer.total_amount || 0) / (offer.price_per_adult || offer.total_amount || 1)));
  const shown = price ?? offer.price_per_adult;
  const off = price != null ? instantOff(price, config) : Math.round(instantOff(offer.total_amount, config) / n);
  return (
    <div>
      {off > 0 && <div className="text-xs text-slate-400 line-through font-mono" data-testid="flight-price-original">{money(shown, offer.currency)}</div>}
      <div className="font-mono text-2xl font-extrabold text-slate-900 tracking-tight" data-testid="flight-price">{money(shown - off, offer.currency)}</div>
      {off > 0 && <div className="text-[10px] font-bold text-[#0F9D58]" data-testid="flight-price-deal">₹{off.toLocaleString("en-IN")} instant off</div>}
    </div>
  );
};

export const AirlineLogo = ({ code, logo, size = "h-9 w-9" }) => {
  const [bg, fg] = AIRLINE_COLORS[code] || ["#334155", "#fff"];
  if (logo) return <img src={logo} alt={code} className={`${size} rounded-lg object-contain bg-white border border-slate-100 shrink-0`} />;
  return (
    <span className={`${size} rounded-lg grid place-items-center font-heading font-extrabold text-xs shrink-0`} style={{ background: bg, color: fg }}>
      {code}
    </span>
  );
};

export const SliceRow = ({ s }) => (
  <div className="flex items-center gap-4 flex-1 min-w-0">
    <div className="text-right sm:text-left">
      <div className="font-heading text-xl font-bold text-slate-900">{time(s.depart_at)}</div>
      <div className="text-xs text-slate-500">{s.origin}</div>
    </div>
    <div className="flex-1 text-center min-w-[90px]">
      <div className="text-xs text-slate-500">{dur(s.duration_min)}</div>
      <div className="relative h-px bg-slate-300 my-1.5">
        {s.stops > 0 && <span className="absolute left-1/2 -translate-x-1/2 -top-1 h-2 w-2 rounded-full bg-[#EC5B24]" />}
      </div>
      <div className={`text-xs font-medium ${s.stops ? "text-[#EC5B24]" : "text-[#0F9D58]"}`}>
        {s.stops ? `${s.stops} stop${s.via ? ` · ${s.via}` : ""}` : "Non-stop"}
      </div>
    </div>
    <div>
      <div className="font-heading text-xl font-bold text-slate-900">
        {time(s.arrive_at)}
        {s.arrive_at.slice(0, 10) !== s.depart_at.slice(0, 10) && <sup className="text-[10px] text-[#EC5B24] ml-0.5">+1</sup>}
      </div>
      <div className="text-xs text-slate-500">{s.destination}</div>
    </div>
  </div>
);

export const FlightCard = ({ offer, onBook, cheapest, fastest, price, priceLabel = "per adult" }) => (
  <div className="bg-white rounded-2xl border border-slate-100 hover:border-[#EC5B24]/40 hover:shadow-[0_8px_30px_-12px_rgba(236,91,36,0.35)] transition-[box-shadow,border-color] p-4 sm:p-5" data-testid={`flight-card-${offer.id}`}>
    <div className="flex flex-wrap gap-2 mb-3 empty:hidden">
      {cheapest && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#E8F5E9] text-[#0F9D58] rounded-full px-2 py-0.5">Cheapest</span>}
      {fastest && <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-600 rounded-full px-2 py-0.5">Fastest</span>}
      {offer.best && <span className="text-[10px] font-bold uppercase tracking-wider bg-orange-50 text-[#EC5B24] rounded-full px-2 py-0.5">Best</span>}
      {offer.refundable && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">Refundable</span>}
    </div>
    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
      <div className="flex items-center gap-3 lg:w-44">
        <AirlineLogo code={offer.slices[0].airline_code} logo={offer.slices[0].airline_logo} />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{[...new Set(offer.slices.map((s) => s.airline_name).filter(Boolean))].join(" + ") || offer.airline_name}</div>
          <div className="text-xs text-slate-500">{offer.slices.map((s) => s.flight_no).join(" / ")}</div>
        </div>
      </div>
      <div className="flex-1 space-y-3">
        {offer.slices.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            {offer.slices.length > 1 && <span className="text-[10px] font-bold text-slate-400 w-10">{i ? "RETURN" : "DEPART"}<br />{dateLabel(s.depart_at, { day: "numeric", month: "short" })}</span>}
            <SliceRow s={s} />
          </div>
        ))}
      </div>
      <div className="flex lg:flex-col items-center lg:items-end justify-between gap-2 lg:w-40 border-t lg:border-t-0 pt-3 lg:pt-0">
        <div className="lg:text-right">
          <DealPrice offer={offer} price={price} />
          <div className="text-[11px] text-slate-500">{priceLabel}</div>
        </div>
        <button data-testid={`book-btn-${offer.id}`} onClick={() => onBook(offer)}
          className="rounded-full bg-[#EC5B24] hover:bg-[#D84A15] active:scale-95 text-white text-sm font-bold px-6 py-2 transition-[background-color,transform]">Book</button>
      </div>
    </div>
    <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
      <span className="flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5 text-[#0F9D58]" /> {offer.baggage}</span>
      {offer.free_meal && <span className="flex items-center gap-1"><Utensils className="h-3.5 w-3.5" /> Free meal</span>}
      {offer.seats_left && <span className="text-red-500 font-medium">Only {offer.seats_left} seats left</span>}
    </div>
  </div>
);
